import { useState, useCallback } from 'react';
import { IMultiLanguageValue } from '../../../../common/interfaces/MultiLanguageValue.interface';
import { ILanguageOption } from '../../../../common/interfaces/LanguageOption.interface';
import { languageOptions } from '../../../../common/constants/languageOptions';
import {
  UseMultiLanguageInputOptions,
  UseMultiLanguageInputReturn,
  ValidationRules,
  ValidationResult,
} from '../types';
import {
  getMultiLangFieldValue,
  updateMultiLangFieldData,
  hasValueForLanguage,
  getLanguagesWithValues,
  clearLanguageValue,
  validateMultiLangField,
} from '../utils/multiLanguageHelpers';
import i18n from 'i18next';

/**
 * useMultiLanguageInput - Central hook for multi-language input state and utilities
 *
 * **Hook Type:** Custom React Hook (Stateful)
 *
 * **Purpose:**
 * Provides consistent language selection and data manipulation utilities for all
 * multi-language components. Eliminates duplicate logic across components.
 *
 * **State Management:**
 * - Manages selected language state internally
 * - Auto-initializes to current i18n language or first available
 * - Provides utility functions for data operations (get, update, validate, clear)
 *
 * **Usage:**
 * ```tsx
 * const {
 *   selectedLanguage,        // Current language object
 *   setSelectedLanguage,     // Change current language
 *   getFieldValue,           // Get value for specific language
 *   updateFieldData,         // Update value for language
 *   validateField,           // Validate value against rules
 * } = useMultiLanguageInput();
 * ```
 *
 * **Options:**
 * ```tsx
 * useMultiLanguageInput({
 *   initialLanguage: 'ar',      // Start with specific language
 *   autoCleanEmpty: true        // Auto-remove empty values (default: true)
 * });
 * ```
 *
 * **Returns:**
 * - selectedLanguage: ILanguageOption - Currently selected language
 * - setSelectedLanguage: (lang) => void - Update selected language
 * - getFieldValue: (data, langCode?) => string - Get value for language
 * - updateFieldData: (data, langCode, value) => IMultiLanguageValue[] - Update value
 * - validateField: (value, rules) => ValidationResult - Validate against rules
 * - checkHasValueForLanguage: (data, langCode?) => boolean - Check if value exists
 * - getLanguagesWithValues: (data) => string[] - Get all languages with values
 * - clearLanguageValue: (data, langCode) => IMultiLanguageValue[] - Remove language entry
 *
 * **When to Use:**
 * Use in ANY component that needs multi-language input support. Required for
 * MultiLanguageField, MultiLanguageFieldGroup, and MultiLanguageList components.
 *
 * @param options - Configuration options for hook behavior
 * @returns Object with language state and utility functions
 */
export const useMultiLanguageInput = (
  options: UseMultiLanguageInputOptions = {}
): UseMultiLanguageInputReturn => {
  const { initialLanguage, autoCleanEmpty = true } = options;

  // Initialize selected language
  const getInitialLanguage = (): ILanguageOption => {
    if (initialLanguage) {
      const langOption = languageOptions.find(
        (lang) => lang.languageCode === initialLanguage
      );
      if (langOption) return langOption;
    }

    // Try to use current i18n language
    const currentLang = i18n.language;
    const currentLangOption = languageOptions.find(
      (lang) => lang.languageCode === currentLang
    );
    if (currentLangOption) return currentLangOption;

    // Default to first option
    return languageOptions[0];
  };

  const [selectedLanguage, setSelectedLanguage] =
    useState<ILanguageOption>(getInitialLanguage());

  /**
   * Get field value for specific language or current selected language
   */
  const getFieldValue = useCallback(
    (fieldData: IMultiLanguageValue[], languageCode?: string): string => {
      const code = languageCode || selectedLanguage.languageCode;
      return getMultiLangFieldValue(fieldData, code);
    },
    [selectedLanguage.languageCode]
  );

  /**
   * Update field data for a specific language
   */
  const updateFieldData = useCallback(
    (
      fieldData: IMultiLanguageValue[],
      languageCode: string,
      value: string
    ): IMultiLanguageValue[] => {
      return updateMultiLangFieldData(
        fieldData,
        languageCode,
        value,
        autoCleanEmpty
      );
    },
    [autoCleanEmpty]
  );

  /**
   * Validate a field value against rules
   */
  const validateField = useCallback(
    (value: string, rules: ValidationRules): ValidationResult => {
      return validateMultiLangField(value, rules);
    },
    []
  );

  /**
   * Check if value exists for language
   */
  const checkHasValueForLanguage = useCallback(
    (fieldData: IMultiLanguageValue[], languageCode: string): boolean => {
      return hasValueForLanguage(fieldData, languageCode);
    },
    []
  );

  /**
   * Get all languages with values
   */
  const getLanguages = useCallback(
    (fieldData: IMultiLanguageValue[]): string[] => {
      return getLanguagesWithValues(fieldData);
    },
    []
  );

  /**
   * Clear value for specific language
   */
  const clearValue = useCallback(
    (
      fieldData: IMultiLanguageValue[],
      languageCode: string
    ): IMultiLanguageValue[] => {
      return clearLanguageValue(fieldData, languageCode);
    },
    []
  );

  return {
    selectedLanguage,
    setSelectedLanguage,
    getFieldValue,
    updateFieldData,
    validateField,
    hasValueForLanguage: checkHasValueForLanguage,
    getLanguagesWithValues: getLanguages,
    clearLanguageValue: clearValue,
  };
};
